import type React from 'react'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import {
  type LoginParams,
  type RegisterParams,
  getUserService,
  loginService,
  registerService,
} from '../services/authService'
import type { FetchErrorType } from '../services/fetch'
import { profileService } from '../services/profileService'
import { userService } from '../services/userService'
import type { Contact, UserProfile, User as UserType } from '../types/user'

// Define the shape of your user state
export interface User {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  stakeholder_types: string[]
  user_type: string // ✅ NEW: Individual position
  phone_number?: string
  promo_code?: string
  logo_url?: string | null
  profile_image_url?: string | null
  has_completed_driver_onboarding: boolean
  has_completed_contractor_onboarding: boolean
  current_profile: {
    id: number
    stakeholder_type: string
  }
  company?: {
    id: number
    name: string
    address: string
    logo_url?: string | null
  } | null
  eid?: string | null
  stripe_customer_id?: string // Stripe customer ID for billing
}

type LoginType = ({ data: { user: User } } & FetchErrorType) | FetchErrorType

export interface UserContextType {
  user: User | null
  setUser: (user: User | null) => void
  logout: () => void
  login: ({ email, password }: LoginParams) => Promise<LoginType>
  fetchUser: () => Promise<User>
  register: (data: RegisterParams) => Promise<LoginType>
  currentUser: User | null
  switchProfile: (profileId: number) => Promise<void>
  refreshUser: () => Promise<void>
  userProfiles: UserProfile[]
  isProfileSwitching: boolean
  // Cached contacts and team members
  contacts: Contact[]
  teamMembers: UserType[]
  refreshContacts: () => Promise<void>
  refreshTeamMembers: () => Promise<void>
  isLoadingContacts: boolean
  isLoadingTeamMembers: boolean
}

// Create the context
const UserContext = createContext<UserContextType | undefined>(undefined)

// Provider component
interface UserProviderProps {
  children: ReactNode
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [userProfiles, setUserProfiles] = useState<UserProfile[]>([])
  const [isProfileSwitching, setIsProfileSwitching] = useState(false)

  // Cached contacts and team members
  const [contacts, setContacts] = useState<Contact[]>([])
  const [teamMembers, setTeamMembers] = useState<UserType[]>([])
  const [isLoadingContacts, setIsLoadingContacts] = useState(false)
  const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(false)
  const [hasLoadedContacts, setHasLoadedContacts] = useState(false)
  const [hasLoadedTeamMembers, setHasLoadedTeamMembers] = useState(false)

  const refreshContacts = useCallback(async () => {
    setIsLoadingContacts(true)
    try {
      const data = await userService.getContacts()
      setContacts(data)
      setHasLoadedContacts(true)
    } catch (error) {
      console.error('Failed to fetch contacts:', error)
    } finally {
      setIsLoadingContacts(false)
    }
  }, [])

  const refreshTeamMembers = useCallback(async () => {
    setIsLoadingTeamMembers(true)
    try {
      const data = await userService.getTeamMembers()
      setTeamMembers(data)
      setHasLoadedTeamMembers(true)
    } catch (error) {
      console.error('Failed to fetch team members:', error)
    } finally {
      setIsLoadingTeamMembers(false)
    }
  }, [])

  // Fetch contacts and team members when user is set (logged in)
  useEffect(() => {
    if (user && !hasLoadedContacts) {
      refreshContacts()
    }
    if (user && !hasLoadedTeamMembers) {
      refreshTeamMembers()
    }
  }, [
    user,
    hasLoadedContacts,
    hasLoadedTeamMembers,
    refreshContacts,
    refreshTeamMembers,
  ])

  const login = async ({ email, password }: LoginParams) => {
    const res = await loginService({ email, password })
    if (!res.error) {
      let userData = res.data.user

      // Fetch user profiles after login
      if (res.data.user?.id) {
        try {
          const profiles = await profileService.getUserProfiles(
            res.data.user.id,
          )
          setUserProfiles(profiles)

          // If user doesn't have current_profile, set it from the is_current profile
          if (!userData.current_profile && profiles.length > 0) {
            const currentProfile =
              profiles.find((p) => p.is_current) || profiles[0]
            userData = {
              ...userData,
              current_profile: {
                id: currentProfile.id,
                stakeholder_type: currentProfile.stakeholder_type,
              },
            }
          }
        } catch (error) {
          console.error('Failed to fetch user profiles:', error)
          setUserProfiles([])
        }
      }

      setUser(userData)
    }
    return res
  }

  const fetchUser = useCallback(async () => {
    const res = await getUserService()
    if (!res.error) {
      let userData = res.data.user

      // Fetch user profiles
      if (res.data.user?.id) {
        try {
          const profiles = await profileService.getUserProfiles(
            res.data.user.id,
          )
          setUserProfiles(profiles)

          // If user doesn't have current_profile, set it from the is_current profile
          if (!userData.current_profile && profiles.length > 0) {
            const currentProfile =
              profiles.find((p) => p.is_current) || profiles[0]
            userData = {
              ...userData,
              current_profile: {
                id: currentProfile.id,
                stakeholder_type: currentProfile.stakeholder_type,
              },
            }
          }
        } catch (error) {
          console.error('Failed to fetch user profiles:', error)
          setUserProfiles([])
        }
      }

      setUser(userData)
      return userData
    }
    return null
  }, [])

  const refreshUser = useCallback(async () => {
    await fetchUser()
  }, [fetchUser])

  const switchProfile = useCallback(
    async (profileId: number) => {
      if (!user?.id) return

      setIsProfileSwitching(true)
      try {
        await profileService.switchProfile(user.id, profileId)
        // Refresh user data to get updated current_profile
        await refreshUser()
      } catch (error) {
        console.error('Failed to switch profile:', error)
        throw error
      } finally {
        setIsProfileSwitching(false)
      }
    },
    [user?.id, refreshUser],
  )

  // Function to log out the user
  const logout = () => {
    setUser(null)
    setUserProfiles([])
    // Clear cached data
    setContacts([])
    setTeamMembers([])
    setHasLoadedContacts(false)
    setHasLoadedTeamMembers(false)
  }

  const register = async (data: RegisterParams) => {
    return await registerService(data)
  }

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        logout,
        login,
        fetchUser,
        register,
        currentUser: user,
        switchProfile,
        refreshUser,
        userProfiles,
        isProfileSwitching,
        contacts,
        teamMembers,
        refreshContacts,
        refreshTeamMembers,
        isLoadingContacts,
        isLoadingTeamMembers,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

// Custom hook to use the UserContext
export const useUser = () => {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}
